/**
 * One tap / click / Enter on a legal hand card plays it: the card is raised
 * into the throw overlay and POST /in-play is sent straight from the event
 * handler. There is no second "confirm" click.
 *
 * Double submits are blocked by a ref (not state) so a second click that
 * lands before React re-renders, or a StrictMode double render, can never
 * start a second request. The lock is released when the server rejects the
 * play, or once the server state no longer has the card in the player's hand.
 * The backend stays the authority on legality; the local check only mirrors
 * the disabled state shown in the hand.
 */
import React, { useCallback, useEffect, useRef } from 'react';
import { isEqual } from 'lodash';
import { useHttpMutation } from '@/api';
import { useAlert } from '@/shared/components';
import { API_URLS } from '../consts';
import { Card, GameInfo, ThrowCardResponse } from '../types';
import { SendMessage } from '../use-websocket';
import type { CardPosition } from './components/cards-with-layout';
import { useThrowCard } from './context';

export type SelectedCard = { card: Card; pos: CardPosition } | null;

/** Time the raised card spins before peers are told to refresh. */
export const THROW_ANIMATION_MS = 700;

const sameCard = (a: Card, b: Card) =>
  a.suite === b.suite && a.value === b.value;

/** True when every card in the hand must be disabled for this state. */
export const isHandLocked = (
  gameInfo: GameInfo | null | undefined,
  userID: string
) =>
  !!gameInfo?.inPlayCards?.some(card => card.userID === userID) ||
  gameInfo?.room?.nextTurn !== userID ||
  !gameInfo?.room?.isFull;

/** Must-follow-suit rule: off-suit cards are blocked while holding the led suit. */
export const isOffSuitBlocked = (
  card: Card,
  ledSuit: Card['suite'] | undefined,
  userDeck: Card[] | undefined
) =>
  !!ledSuit &&
  card.suite !== ledSuit &&
  !!userDeck?.some(({ suite }) => suite === ledSuit);

export const isCardPlayable = (
  card: Card,
  gameInfo: GameInfo | null | undefined,
  userID: string,
  userDeck: Card[]
) =>
  !isHandLocked(gameInfo, userID) &&
  userDeck.some(c => sameCard(c, card)) &&
  !isOffSuitBlocked(card, gameInfo?.inPlayCards?.[0]?.suite, userDeck);

interface UsePlayCardArgs {
  gameInfo: GameInfo | null | undefined;
  userID: string;
  userDeck: Card[];
  sendMessage: SendMessage;
  setSelectedCard: React.Dispatch<React.SetStateAction<SelectedCard>>;
}

export const usePlayCard = ({
  gameInfo,
  userID,
  userDeck,
  sendMessage,
  setSelectedCard,
}: UsePlayCardArgs) => {
  const { mutate: throwCard } = useHttpMutation<ThrowCardResponse, Card>(
    API_URLS.IN_PLAY,
    'POST'
  );
  const { setThrownCard } = useThrowCard();
  const { addAlert } = useAlert();

  const pendingRef = useRef<Card | null>(null);
  const mountedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Set inside the effect (not at init) so StrictMode's mount → unmount →
  // mount cycle leaves it true.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, []);

  // The server accepted the play once the card has left the hand.
  useEffect(() => {
    const pending = pendingRef.current;
    if (pending && !userDeck.some(c => sameCard(c, pending))) {
      pendingRef.current = null;
    }
  }, [userDeck]);

  // Latest state for the event handler, so a click always validates against
  // the state that is on screen rather than a stale closure.
  const latest = useRef({ gameInfo, userID, userDeck, sendMessage });
  latest.current = { gameInfo, userID, userDeck, sendMessage };

  const playCard = useCallback(
    (card: Card, pos: CardPosition) => {
      const { gameInfo, userID, userDeck } = latest.current;
      if (pendingRef.current) return false;
      if (!isCardPlayable(card, gameInfo, userID, userDeck)) return false;

      pendingRef.current = card;
      setSelectedCard({ card, pos });

      throwCard(card, {
        onSuccess: () => {
          if (!mountedRef.current) return;
          setThrownCard(card);
          timerRef.current = setTimeout(() => {
            timerRef.current = null;
            if (!mountedRef.current) return;
            latest.current.sendMessage('throw-card');
          }, THROW_ANIMATION_MS);
        },
        onError: err => {
          pendingRef.current = null;
          if (!mountedRef.current) return;
          // Put the card back in the hand so the player can retry.
          setSelectedCard(prev =>
            prev && isEqual(prev.card, card) ? null : prev
          );
          // useHttpMutation rethrows `err.response`, so read both shapes.
          const res = ((err as { response?: unknown })?.response ?? err) as
            | { status?: number; data?: { message?: string } }
            | undefined;
          if (res?.status === 422) {
            addAlert({
              message:
                res.data?.message ||
                'That card cannot be played now. Check the turn and led suit.',
              type: 'warning',
            });
            return;
          }
          addAlert({
            message: res?.data?.message || 'Something went wrong',
            type: 'error',
          });
        },
      });
      return true;
    },
    [addAlert, setSelectedCard, setThrownCard, throwCard]
  );

  return { playCard };
};
